import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtUsd,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { rent: '', opex: '', debt: '' };

export function ToolCashFlow() {
  const [v, setV] = useState(DEFAULTS);

  const cashFlow = useMemo(() => {
    const income = parseNum(v.rent);
    const opex = parseNum(v.opex);
    const debt = parseNum(v.debt);
    if (income <= 0 && opex <= 0 && debt <= 0) return null;
    return income - opex - debt;
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Cash flow calculator"
        subtitle="Calculate monthly cash flow for your multifamily property investment."
      />

      <InfoSection title="How to use this calculator">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Monthly rental income</strong> — Total scheduled rent before
            vacancy (all units).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Operating expenses</strong> — Property taxes, insurance,
            repairs, management, utilities not rebilled, etc. (monthly).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Debt service</strong> — Principal and interest on your
            mortgage (monthly).
          </li>
        </ul>
        <ExampleCallout>
          Example: $8,000 rent − $2,500 opex − $3,500 debt = <strong className="text-[var(--home-text-on-dark)]">$2,000</strong>{' '}
          monthly cash flow.
        </ExampleCallout>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Income &amp; expenses</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cf-rent" tip="Total monthly rent from all units." required>
                  Monthly rental income
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="cf-rent"
                    type="text"
                    inputMode="decimal"
                    value={v.rent}
                    onChange={(e) => setV((s) => ({ ...s, rent: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cf-opex" tip="Total monthly operating expenses before debt." required>
                  Operating expenses
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="cf-opex"
                    type="text"
                    inputMode="decimal"
                    value={v.opex}
                    onChange={(e) => setV((s) => ({ ...s, opex: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cf-debt" tip="Monthly mortgage principal and interest." required>
                  Debt service
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="cf-debt"
                    type="text"
                    inputMode="decimal"
                    value={v.debt}
                    onChange={(e) => setV((s) => ({ ...s, debt: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Cash flow results</PanelTitle>
            {cashFlow === null ? (
              <ResultsEmpty message="Enter rental income, expenses, and debt service to calculate cash flow." />
            ) : (
              <div className="flex min-h-[220px] flex-col justify-center space-y-2">
                <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">Monthly cash flow</p>
                <p
                  className={`font-display text-3xl font-bold ${cashFlow >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}
                >
                  {fmtUsd(cashFlow)}
                </p>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
